import React, { useEffect, useRef, useState } from 'react';
import { FiPause, FiPlay } from 'react-icons/fi';

interface AudioPlayerProps {
  audioUrl: string;
  duration: number;
  autoPlay?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, duration, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch((error) => {
              console.error('Audio playback failed:', error);
              setIsPlaying(false);
            });
        }
      }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setIsPlaying(false);
    setCurrentTime(0);
    audio.load();

    const handleEnded = () => setIsPlaying(false);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!autoPlay || !audioRef.current || isPlaying) {
      return;
    }

    const audio = audioRef.current;
    const playWhenReady = () => {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((error) => {
          console.error('Auto playback failed:', error);
        });
      audio.removeEventListener('canplaythrough', playWhenReady);
    };

    if (audio.readyState >= 3) {
      playWhenReady();
    } else {
      audio.addEventListener('canplaythrough', playWhenReady);
    }
  }, [audioUrl, autoPlay, isPlaying]);

  return (
    <div className="audio-player-container">
      <button className="audio-play-button" onClick={togglePlay}>
        {isPlaying ? <FiPause /> : <FiPlay />}
      </button>
      <div className="audio-waveform">
        <div
          className="audio-progress"
          style={{
            width: `${(currentTime / duration) * 100}%`,
          }}
        />
      </div>
      <span className="audio-duration">{formatTime(isPlaying ? currentTime : duration)}</span>
      <audio ref={audioRef} src={audioUrl} />
    </div>
  );
};

export default AudioPlayer;
